/* Toggles .secondaryNavHidden and #site-navigation.fixedMenuAnimated independently, each with its own live-measured threshold, rAF-throttled - matches the parent theme's fixate-menu.js approach (threshold = where <main> naturally sits in the document, adapting to header type/height automatically) but split in two: the nav switches to its "scrolled" look once main touches its own bottom edge, while the secondary/highlights bar hides earlier - as soon as main touches *its* bottom edge (nav height + bar's own natural height). Hovering the main navbar temporarily brings the secondary bar back while scrolled. */
(() => {
    const bar = document.getElementById('secondary-navigation');
    const nav = document.getElementById('site-navigation');
    const main = document.querySelector('main');
    if (!bar && !nav) {
        return;
    }

    let ticking = false;
    let navScrolled = false;
    let barScrolled = false;
    let hovering = false;

    // Captured once, before any scroll-driven class ever toggles - the
    // unscrolled nav height. nav.offsetHeight itself changes the instant
    // fixedMenuAnimated toggles (padding shrinks), so using it live in the
    // threshold math creates a feedback loop right at the boundary: crossing
    // the threshold changes the height the threshold is computed from, which
    // can immediately want to toggle back, causing rapid flicker.
    const baseNavHeight = nav ? nav.offsetHeight : 0;

    function getMainTop() {
        return main ? main.getBoundingClientRect().top + window.scrollY : null;
    }

    function getNavThreshold() {
        const mainTop = getMainTop();
        if (mainTop === null || !nav) {
            return 20;
        }

        return Math.max(mainTop - baseNavHeight, 0);
    }

    function getBarThreshold() {
        const mainTop = getMainTop();
        if (mainTop === null || !nav) {
            return 20;
        }

        const barHeight = bar ? bar.scrollHeight : 0;
        return Math.max(mainTop - baseNavHeight - barHeight, 0);
    }

    function syncNavbarHeight() {
        if (!nav) {
            return;
        }

        if (window.innerWidth >= 1366) {
            document.documentElement.style.setProperty('--navbar-height', nav.offsetHeight + 'px');
        } else {
            document.documentElement.style.removeProperty('--navbar-height');
        }

        if (bar) {
            bar.style.top = nav.offsetHeight + 'px';
        }
    }

    function apply() {
        if (bar) {
            bar.classList.toggle('secondaryNavHidden', barScrolled && !hovering);
        }

        if (nav) {
            nav.classList.toggle('fixedMenuAnimated', navScrolled);
        }

        syncNavbarHeight();
    }

    if (nav && window.ResizeObserver) {
        new ResizeObserver(syncNavbarHeight).observe(nav);
    }

    function update() {
        const shouldNavScroll = window.scrollY >= getNavThreshold();
        const shouldBarScroll = window.scrollY >= getBarThreshold();
        if (shouldNavScroll !== navScrolled || shouldBarScroll !== barScrolled) {
            navScrolled = shouldNavScroll;
            barScrolled = shouldBarScroll;
            apply();
        }
        ticking = false;
    }

    document.addEventListener('scroll', () => {
        if (!ticking) {
            ticking = true;
            requestAnimationFrame(update);
        }
    }, { passive: true });

    window.addEventListener('resize', () => {
        if (!ticking) {
            ticking = true;
            requestAnimationFrame(() => {
                update();
                apply();
                ticking = false;
            });
        }
    }, { passive: true });

    const menuToggler = nav ? nav.querySelector('#navbar-button') : null;
    const togglerLeewayPx = 16;

    function isInsideHoverGroup(el) {
        return !!el && ((nav && nav.contains(el)) || (bar && bar.contains(el)));
    }

    // The "stay hidden" zone isn't just the toggler's own box - it's the
    // toggler plus a bit of leeway on both sides, but across the navbar's
    // full height (not just the button's own shorter height), so the
    // vertical padding above/below the toggler doesn't trigger the reveal
    // either. Deliberately NOT extended out to the navbar's own right edge -
    // that swallowed unrelated elements further right (search/language icons)
    // into the dead zone too.
    function isInTogglerZone(clientX, clientY) {
        if (!menuToggler || !nav) {
            return false;
        }

        const togglerRect = menuToggler.getBoundingClientRect();
        const navRect = nav.getBoundingClientRect();

        return clientX >= togglerRect.left - togglerLeewayPx
            && clientX <= togglerRect.right + togglerLeewayPx
            && clientY >= navRect.top
            && clientY <= navRect.bottom;
    }

    let hoverZoneTicking = false;

    function updateHoverZone(clientX, clientY) {
        const nextHovering = !isInTogglerZone(clientX, clientY);
        if (nextHovering !== hovering) {
            hovering = nextHovering;
            apply();
        }
    }

    function handleMouseEnter(event) {
        updateHoverZone(event.clientX, event.clientY);
    }

    function handleNavMouseMove(event) {
        if (!hoverZoneTicking) {
            hoverZoneTicking = true;
            const { clientX, clientY } = event;
            requestAnimationFrame(() => {
                updateHoverZone(clientX, clientY);
                hoverZoneTicking = false;
            });
        }
    }

    function handleMouseLeave(event) {
        if (isInsideHoverGroup(event.relatedTarget)) {
            return;
        }

        hovering = false;
        apply();
    }

    if (nav) {
        nav.addEventListener('mouseenter', handleMouseEnter);
        nav.addEventListener('mousemove', handleNavMouseMove);
        nav.addEventListener('mouseleave', handleMouseLeave);
    }

    if (bar) {
        bar.addEventListener('mouseenter', handleMouseEnter);
        bar.addEventListener('mouseleave', handleMouseLeave);
    }

    update();
    apply();
})();
